/**
 * AI Analysis Service
 * Service for handling AI analysis API calls
 */

import { AI } from '../config/api';
import { authFetch } from './authFetch';

export interface PredictionInsight {
  predicted_outcome: string;
  confidence_score: number;
  time_to_impact: string;
  impact_scope: string;
}

export interface RecommendationInsight {
  recommended_action: string;
  expected_resolution_time: string;
  success_rate: number;
  action_priority: string;
}

export interface EscalationRiskInsight {
  escalation_risk_score: number;
  historical_escalation_rate: string;
  recommended_preventive_action: string;
}

export interface RootCauseInsight {
  predicted_root_cause: string;
  related_components: string[];
  confidence_score: number;
}

export interface ReliabilityImprovementInsight {
  recommendation: string;
  impact_estimate: string;
  priority_level: string;
}

export interface AIAnalysisResult {
  prediction: PredictionInsight;
  recommendation: RecommendationInsight;
  escalation_risk: EscalationRiskInsight;
  root_cause: RootCauseInsight;
  reliability_improvement: ReliabilityImprovementInsight;
  analysis_timestamp: string;
  analysis_duration_seconds: number;
  scope_name: string;
  performance_summary: string;
}

export interface AIAnalysisResponse {
  success: boolean;
  data?: AIAnalysisResult;
  error?: string;
  message?: string;
  timestamp: string;
}

export interface AIHealthResponse {
  success: boolean;
  data: {
    status: 'healthy' | 'unhealthy';
    model?: string;
    message: string;
    timestamp: string;
  };
}

/**
 * Analyze alert data using AI
 */
export const analyzeAlerts = async (hedwigData: any): Promise<AIAnalysisResponse> => {
  try {
    console.log('📊 Requesting AI analysis for alert data...');
    
    const response = await authFetch(AI.ANALYZE_ALERTS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Send the hedwigData directly - it already has the correct structure
      // The backend now accepts both {"data": ...} and {"alert_data": ...} formats
      body: JSON.stringify(hedwigData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    console.log('✅ AI analysis completed successfully');
    return data;
  } catch (error) {
    console.error('❌ AI analysis failed:', error);
    throw error;
  }
};

/**
 * Check AI service health
 */
export const checkAIHealth = async (): Promise<AIHealthResponse> => {
  try {
    const response = await authFetch(AI.HEALTH, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ AI health check failed:', error);
    throw error;
  }
};

/**
 * Get AI service configuration
 */
export const getAIConfig = async () => {
  try {
    const response = await authFetch(AI.CONFIG, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('❌ Failed to get AI config:', error);
    throw error;
  }
};